import React, { useEffect, useImperativeHandle, useState } from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import KatakanaTextField from 'components/form/KatakanaTextField';
import PartnerSelect from 'components/form/PartnerSelect';
import TextArea from 'components/form/TextArea';
import UserSelect from 'components/form/UserSelect';
import { MSG_MAX_LENGTH, MSG_REQUIRED } from 'constant/ValidationMessages';
import {
  MasterTypeResult,
  getContractType,
  getEmploymentType,
  getGender,
} from 'stores/MasterTypeStoreAdapter';
import { createMember } from 'stores/MemberStoreAdapter';
import { PartnerResult, listPartner } from 'stores/PartnerStoreAdapter';
import { UserResult, getUser } from 'stores/UserStoreAdapter';
import { errorDefault, successDefault } from 'utils/Toastify';

export type MemberCreateFormHandle = {
  submit: () => void;
};

type MemberCreateFormProps = {
  onCreate: () => void;
  onCancel: () => void;
  setProgress: (v: boolean) => void;
  /** コンソールドロワー内で使う場合など、フォーム内蔵ヘッダーを非表示にする（既定: false） */
  hideHeader?: boolean;
};

/** DetailPanel の p:3 (24px) を打ち消す sticky ヘッダー sx */
const STICKY_HEADER_SX = {
  position: 'sticky',
  top: -24,
  zIndex: 2,
  bgcolor: 'background.paper',
  borderBottom: 1,
  borderColor: 'divider',
  pb: 1,
  mx: -3,
  px: 3,
  pt: 3,
  mt: -3,
} as const;

const MemberCreateForm = React.forwardRef<MemberCreateFormHandle, MemberCreateFormProps>(
  ({ onCreate, onCancel, setProgress, hideHeader = false }, ref) => {
    const [memberName, setMemberName] = useState('');
    const [memberNameKana, setMemberNameKana] = useState('');
    const [employmentTypeId, setEmploymentTypeId] = useState('');
    const [userId, setUserId] = useState('');
    const [genderId, setGenderId] = useState('');
    const [isIndividual, setIsIndividual] = useState(false);
    const [department, setDepartment] = useState('');
    const [partnerId, setPartnerId] = useState('');
    const [contractTypeId, setContractTypeId] = useState('');
    const [remarks, setRemarks] = useState('');

    const [employmentTypes, setEmploymentTypes] = useState<MasterTypeResult[]>([]);
    const [genders, setGenders] = useState<MasterTypeResult[]>([]);
    const [contractTypes, setContractTypes] = useState<MasterTypeResult[]>([]);
    const [partners, setPartners] = useState<PartnerResult[]>([]);
    const [users, setUsers] = useState<UserResult[]>([]);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const isEmployee = employmentTypeId === 'EMPLOYEE';

    useEffect(() => {
      Promise.all([getEmploymentType(), getGender(), getContractType(), getUser({})])
        .then(([empTypes, genTypes, conTypes, userList]) => {
          setEmploymentTypes(empTypes);
          setGenders(genTypes);
          // 契約形態から「社員」を除外して表示
          setContractTypes(conTypes.filter((t) => t.typeCode !== 'EMPLOYEE'));
          setUsers(userList);
          // ラジオボタン初期値: 最初の選択肢
          if (empTypes.length > 0) setEmploymentTypeId(empTypes[0].typeCode);
          if (genTypes.length > 0) setGenderId(genTypes[0].typeCode);
        })
        .catch(() => errorDefault('マスタデータの取得に失敗しました'));
    }, []);

    // 雇用形態変更時のリセット
    useEffect(() => {
      if (isEmployee) {
        setIsIndividual(false);
        setPartnerId('');
        setContractTypeId('');
      } else {
        setUserId('');
        setDepartment('');
        // 契約形態ラジオ初期値: 最初の選択肢
        if (contractTypes.length > 0 && !contractTypeId) {
          setContractTypeId(contractTypes[0].typeCode);
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employmentTypeId]);

    // 個人事業主フラグ変更時にパートナー候補を再取得
    useEffect(() => {
      if (!isEmployee && employmentTypeId) {
        const partnerTypeId = isIndividual ? 'INDIVIDUAL' : 'CORPORATE';
        listPartner({ partnerTypeId })
          .then(setPartners)
          .catch(() => errorDefault('パートナー情報の取得に失敗しました'));
        setPartnerId('');
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isIndividual, employmentTypeId]);

    const validate = (): boolean => {
      const newErrors: Record<string, string> = {};

      if (!memberName) newErrors.memberName = MSG_REQUIRED('要員名');
      else if (memberName.length > 300) newErrors.memberName = MSG_MAX_LENGTH('要員名', 300);

      if (!memberNameKana) newErrors.memberNameKana = MSG_REQUIRED('要員名（カナ）');
      else if (memberNameKana.length > 300)
        newErrors.memberNameKana = MSG_MAX_LENGTH('要員名（カナ）', 300);

      if (!employmentTypeId) newErrors.employmentTypeId = MSG_REQUIRED('雇用形態');
      if (!genderId) newErrors.genderId = MSG_REQUIRED('性別');

      if (!isEmployee) {
        if (!partnerId) newErrors.partnerId = MSG_REQUIRED('パートナー');
        if (!contractTypeId) newErrors.contractTypeId = MSG_REQUIRED('契約形態');
      }

      if (remarks && remarks.length > 10000) newErrors.remarks = MSG_MAX_LENGTH('備考', 10000);

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const handleCreate = () => {
      if (!validate()) return;

      setProgress(true);
      createMember({
        memberName,
        memberNameKana,
        employmentTypeId,
        genderId,
        isIndividual,
        ...(userId && { userId }),
        ...(department && { department }),
        ...(partnerId && { partnerId }),
        ...(contractTypeId && { contractTypeId }),
        ...(remarks && { remarks }),
      })
        .then(() => {
          successDefault('要員を作成しました');
          onCreate();
        })
        .catch((e: Error) => {
          errorDefault('要員の作成に失敗しました', e.message);
        })
        .finally(() => setProgress(false));
    };

    useImperativeHandle(ref, () => ({ submit: handleCreate }));

    return (
      <Stack sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {!hideHeader && (
          <Box sx={STICKY_HEADER_SX}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" alignItems="center" spacing={1}>
                <IconButton size="small" onClick={onCancel}>
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
                <Typography variant="h6" fontWeight={700}>
                  要員登録
                </Typography>
              </Stack>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 4.5 }}>
              新しい要員を登録します
            </Typography>
          </Box>
        )}

        <Stack spacing={3} sx={{ flex: 1, overflow: 'auto', pt: hideHeader ? 0 : 2 }}>
          {/* 基本情報 */}
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="text.secondary"
              sx={{ mb: 1.5 }}
            >
              基本情報
            </Typography>
            <Grid container spacing={2}>
              <Grid size={6}>
                <TextField
                  label="要員名"
                  value={memberName}
                  onChange={(e) => setMemberName(e.target.value)}
                  fullWidth
                  size="small"
                  required
                  error={!!errors.memberName}
                  helperText={errors.memberName}
                  slotProps={{ htmlInput: { maxLength: 300 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid size={6}>
                <KatakanaTextField
                  label="要員名（カナ）"
                  value={memberNameKana}
                  onChange={(v) => setMemberNameKana(v)}
                  fullWidth
                  size="small"
                  required
                  error={!!errors.memberNameKana}
                  helperText={errors.memberNameKana}
                  slotProps={{ htmlInput: { maxLength: 300 } }}
                />
              </Grid>
              <Grid size={12}>
                <FormControl error={!!errors.employmentTypeId} required>
                  <FormLabel sx={{ fontSize: '0.875rem', mb: 0.5 }}>雇用形態</FormLabel>
                  <RadioGroup
                    row
                    value={employmentTypeId}
                    onChange={(e) => setEmploymentTypeId(e.target.value)}
                  >
                    {employmentTypes.map((t) => (
                      <FormControlLabel
                        key={t.typeCode}
                        value={t.typeCode}
                        control={<Radio size="small" />}
                        label={t.typeLabel}
                      />
                    ))}
                  </RadioGroup>
                  {errors.employmentTypeId && (
                    <FormHelperText>{errors.employmentTypeId}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid size={12}>
                <FormControl error={!!errors.genderId} required>
                  <FormLabel sx={{ fontSize: '0.875rem', mb: 0.5 }}>性別</FormLabel>
                  <RadioGroup row value={genderId} onChange={(e) => setGenderId(e.target.value)}>
                    {genders.map((t) => (
                      <FormControlLabel
                        key={t.typeCode}
                        value={t.typeCode}
                        control={<Radio size="small" />}
                        label={t.typeLabel}
                      />
                    ))}
                  </RadioGroup>
                  {errors.genderId && <FormHelperText>{errors.genderId}</FormHelperText>}
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* 所属情報 */}
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="text.secondary"
              sx={{ mb: 1.5 }}
            >
              所属
            </Typography>
            <Grid container spacing={2}>
              {isEmployee && (
                <>
                  <Grid size={6}>
                    <UserSelect
                      mode="single"
                      users={users}
                      selectedUserId={userId || null}
                      onChange={(id) => {
                        setUserId(id || '');
                        if (id) {
                          const user = users.find((u) => u.userId === id);
                          if (user) {
                            setMemberName(user.userName);
                          }
                        }
                      }}
                      label="ユーザー"
                    />
                  </Grid>
                  <Grid size={12}>
                    <TextField
                      label="所属"
                      value={department}
                      onChange={(e) => setDepartment(e.target.value)}
                      fullWidth
                      size="small"
                      slotProps={{ htmlInput: { maxLength: 300 } }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Grid>
                </>
              )}
              {!isEmployee && employmentTypeId && (
                <>
                  <Grid size={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isIndividual}
                          onChange={(e) => setIsIndividual(e.target.checked)}
                          size="small"
                        />
                      }
                      label="個人事業主"
                    />
                  </Grid>
                  <Grid size={6}>
                    <PartnerSelect
                      mode="single"
                      partners={partners}
                      selectedPartnerId={partnerId || null}
                      onChange={(id) => setPartnerId(id || '')}
                      label="パートナー"
                      required
                      error={!!errors.partnerId}
                      helperText={errors.partnerId}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Box>

          {/* 契約形態 (社外のみ) */}
          {!isEmployee && employmentTypeId && (
            <>
              <Divider />
              <Box>
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  color="text.secondary"
                  sx={{ mb: 1.5 }}
                >
                  契約
                </Typography>
                <FormControl error={!!errors.contractTypeId} required>
                  <FormLabel sx={{ fontSize: '0.875rem', mb: 0.5 }}>契約形態</FormLabel>
                  <RadioGroup
                    row
                    value={contractTypeId}
                    onChange={(e) => setContractTypeId(e.target.value)}
                  >
                    {contractTypes.map((t) => (
                      <FormControlLabel
                        key={t.typeCode}
                        value={t.typeCode}
                        control={<Radio size="small" />}
                        label={t.typeLabel}
                      />
                    ))}
                  </RadioGroup>
                  {errors.contractTypeId && (
                    <FormHelperText>{errors.contractTypeId}</FormHelperText>
                  )}
                </FormControl>
              </Box>
            </>
          )}

          <Divider />

          {/* 備考 */}
          <TextArea
            label="備考"
            value={remarks}
            onChange={setRemarks}
            maxLength={10000}
            rows={6}
            fullWidth
            size="small"
            errorMessage={errors.remarks}
          />
        </Stack>
      </Stack>
    );
  },
);

MemberCreateForm.displayName = 'MemberCreateForm';

export default MemberCreateForm;
